import type {Dimensions, ExifOrientation} from '../types';

export type OrientationStep =
  | {readonly kind: 'translate'; readonly x: number; readonly y: number}
  | {readonly kind: 'rotate'; readonly quarterTurns: 1 | 2 | -1}
  | {readonly kind: 'scale'; readonly x: 1 | -1; readonly y: 1 | -1};

const SWAPS_DIMENSIONS: Readonly<Record<ExifOrientation, boolean>> = {
  1: false,
  2: false,
  3: false,
  4: false,
  5: true,
  6: true,
  7: true,
  8: true,
};

type StepsBuilder = (dimensions: Dimensions) => readonly OrientationStep[];

/**
 * Canvas transforms that draw a stored image upright, applied in order before
 * `drawImage(image, 0, 0)` on a canvas of the oriented size.
 */
const STEPS_BY_ORIENTATION: Readonly<Record<ExifOrientation, StepsBuilder>> = {
  1: () => [],
  2: ({width}) => [
    {kind: 'translate', x: width, y: 0},
    {kind: 'scale', x: -1, y: 1},
  ],
  3: ({width, height}) => [
    {kind: 'translate', x: width, y: height},
    {kind: 'rotate', quarterTurns: 2},
  ],
  4: ({height}) => [
    {kind: 'translate', x: 0, y: height},
    {kind: 'scale', x: 1, y: -1},
  ],
  5: () => [
    {kind: 'rotate', quarterTurns: 1},
    {kind: 'scale', x: 1, y: -1},
  ],
  6: ({height}) => [
    {kind: 'rotate', quarterTurns: 1},
    {kind: 'translate', x: 0, y: -height},
  ],
  7: ({width, height}) => [
    {kind: 'rotate', quarterTurns: 1},
    {kind: 'translate', x: width, y: -height},
    {kind: 'scale', x: -1, y: 1},
  ],
  8: ({width}) => [
    {kind: 'rotate', quarterTurns: -1},
    {kind: 'translate', x: -width, y: 0},
  ],
};

export const orientationSwapsDimensions = (orientation: ExifOrientation): boolean =>
  SWAPS_DIMENSIONS[orientation];

export const orientedDimensions = (
  dimensions: Dimensions,
  orientation: ExifOrientation,
): Dimensions =>
  SWAPS_DIMENSIONS[orientation] ? {width: dimensions.height, height: dimensions.width} : dimensions;

export const orientationSteps = (
  orientation: ExifOrientation,
  storedDimensions: Dimensions,
): readonly OrientationStep[] => STEPS_BY_ORIENTATION[orientation](storedDimensions);
